// HtmlForms.jsx
import React from "react";

const HtmlForms = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML Forms - Comprehensive Guide</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction to HTML Forms</h2>
        <p>
          HTML forms are used to collect user input and send it to a server for processing. Forms
          contain form elements like text fields, radio buttons, checkboxes, and submit buttons.
        </p>
      </section>

      {/* Form Basics */}
      <section id="form-basics">
        <h2>2. Basic Form Structure</h2>
        <pre>
          <code>
{`<form action="/submit" method="post">
  <label for="name">Name:</label>
  <input type="text" id="name" name="name" />
  
  <label for="email">Email:</label>
  <input type="email" id="email" name="email" />
  
  <input type="submit" value="Submit" />
</form>`}
          </code>
        </pre>
      </section>

      {/* Form Attributes */}
      <section id="form-attributes">
        <h2>3. Form Attributes</h2>
        <ul>
          <li><strong>action:</strong> URL to send form data.</li>
          <li><strong>method:</strong> HTTP method (<code>get</code> or <code>post</code>).</li>
          <li><strong>enctype:</strong> Encoding type for file uploads (<code>multipart/form-data</code>).</li>
          <li><strong>target:</strong> Where to display response (<code>_blank</code>, <code>_self</code>).</li>
          <li><strong>autocomplete:</strong> Enable or disable browser autofill (<code>on</code>/<code>off</code>).</li>
        </ul>
      </section>

      {/* Input Types */}
      <section id="input-types">
        <h2>4. Input Types</h2>
        <ul>
          <li><strong>text:</strong> Single-line text input.</li>
          <li><strong>password:</strong> Hidden text input.</li>
          <li><strong>email:</strong> Email validation.</li>
          <li><strong>number:</strong> Numeric input with min/max.</li>
          <li><strong>date, time, datetime-local:</strong> Date & time pickers.</li>
          <li><strong>checkbox:</strong> Multiple selections.</li>
          <li><strong>radio:</strong> Single selection from a group.</li>
          <li><strong>file:</strong> File upload input.</li>
          <li><strong>submit:</strong> Submit the form.</li>
          <li><strong>reset:</strong> Reset form values.</li>
        </ul>
      </section>

      {/* Textarea */}
      <section id="textarea">
        <h2>5. Textarea</h2>
        <p>Used for multi-line text input.</p>
        <pre>
          <code>
{`<label for="message">Message:</label>
<textarea id="message" name="message" rows="4" cols="50"></textarea>`}
          </code>
        </pre>
      </section>

      {/* Select Dropdown */}
      <section id="select-dropdown">
        <h2>6. Select Dropdown</h2>
        <pre>
          <code>
{`<label for="country">Country:</label>
<select id="country" name="country">
  <option value="usa">USA</option>
  <option value="uk">UK</option>
  <option value="india">India</option>
</select>`}
          </code>
        </pre>
      </section>

      {/* Form Validation */}
      <section id="form-validation">
        <h2>7. Form Validation</h2>
        <ul>
          <li>HTML5 attributes like <code>required</code>, <code>pattern</code>, <code>min</code>, <code>max</code>, <code>maxlength</code>.</li>
          <li>Browsers perform basic validation automatically.</li>
          <li>Example:</li>
        </ul>
        <pre>
          <code>
{`<input type="email" name="userEmail" required placeholder="Enter your email" />`}
          </code>
        </pre>
      </section>

      {/* Form Layout & Fieldsets */}
      <section id="form-layout">
        <h2>8. Fieldsets and Legends</h2>
        <p>Used to group related fields:</p>
        <pre>
          <code>
{`<fieldset>
  <legend>Personal Info</legend>
  <label for="fname">First Name:</label>
  <input type="text" id="fname" name="fname" />
  
  <label for="lname">Last Name:</label>
  <input type="text" id="lname" name="lname" />
</fieldset>`}
          </code>
        </pre>
      </section>

      {/* Buttons */}
      <section id="form-buttons">
        <h2>9. Form Buttons</h2>
        <ul>
          <li><strong>&lt;input type="submit"&gt;</strong> - submits the form.</li>
          <li><strong>&lt;input type="reset"&gt;</strong> - resets the form.</li>
          <li><strong>&lt;button&gt;</strong> - can be used for submit, reset, or custom JavaScript actions.</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>10. Best Practices</h2>
        <ul>
          <li>Use <code>label</code> elements for accessibility.</li>
          <li>Use proper input types for validation.</li>
          <li>Group related fields using <code>fieldset</code>.</li>
          <li>Use semantic HTML and avoid styling tables for layout.</li>
          <li>Ensure forms are responsive for mobile users.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlForms;
